import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Compass, Gift, CheckCircle, Sparkle } from "@phosphor-icons/react";

export default function ReferralLanding() {
  const { code } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!code) return;
    // Stash code so OAuth signup can pick it up after redirect.
    try { localStorage.setItem("cgps_ref", code.toUpperCase()); } catch (storageErr) {
      console.debug("cgps_ref persist failed", storageErr);
    }
    api.get(`/referrals/info/${code}`)
      .then((r) => setInfo(r.data))
      .catch(() => setError(true));
  }, [code]);

  const startSignup = () => nav(`/signup?ref=${encodeURIComponent(code.toUpperCase())}`);

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="referral-landing">
      <div className="px-6 py-6">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="referral-home-link">
          <Compass size={22} weight="duotone" className="text-blue-500" />
          <span className="font-medium tracking-tight">Career GPS <span className="text-blue-500">AI</span></span>
        </Link>
      </div>
      <div className="flex items-center justify-center px-6 py-20">
        <div className="max-w-xl w-full">
          {error ? (
            <div className="bg-[#0F0F12] border border-red-500/30 rounded-2xl p-10 text-center" data-testid="referral-invalid">
              <h1 className="text-3xl font-medium">This referral link is invalid.</h1>
              <p className="text-zinc-500 mt-3">No worries — you can still start free.</p>
              <Link to="/signup" className="inline-block mt-6 bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-medium" data-testid="referral-signup-free">Sign up free</Link>
            </div>
          ) : (
            <div className="relative bg-[#0F0F12] border border-blue-500/40 rounded-2xl p-10 glow-blue overflow-hidden">
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-blue-600/20 blur-3xl rounded-full pointer-events-none" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 px-3 py-1.5 rounded-full text-xs mb-6">
                  <Gift size={12} weight="fill" className="text-blue-400" />
                  <span className="text-blue-200 tracking-wide">Friend invite · Exclusive</span>
                </div>
                <h1 className="text-4xl md:text-5xl tracking-tight font-medium">
                  {info?.name || "A friend"} invited you to <span className="gradient-text">Career GPS AI</span>.
                </h1>
                <p className="text-zinc-400 mt-4 text-lg">
                  Sign up with this link and you both earn <span className="text-white font-medium">7 days of Pro</span> when the referral is successful. The referral reward is separate from the optional 7-day paid Pro trial.
                </p>
                <div className="mt-8 space-y-2 text-sm text-zinc-300">
                  {["AI-generated personalized career roadmap", "Up to 100 AI Coach messages/month on Pro", "3 Resume Reviews + 3 Interview Sessions/month on Pro", "Job matches & salary intel"].map((b) => (
                    <div key={b} className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-400" /> {b}</div>
                  ))}
                </div>
                <button onClick={startSignup} data-testid="referral-cta-signup"
                  className="mt-10 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3.5 rounded-lg inline-flex items-center justify-center gap-2 animate-pulse-glow">
                  <Sparkle size={16} weight="fill" /> Get 7 Pro days
                </button>
                <p className="text-xs text-zinc-600 mt-4 text-center">Code: <span className="font-mono text-blue-400">{info?.code || code}</span></p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
